import type * as go from 'gojs';
import { batch } from 'react-redux';
import { store } from 'createStore';
import {
  updatePanels,
  updatePanelsWithHistory,
} from '../../store/units/reducer';
import type {
  PanelInterface,
  PanelMapInterface,
} from '../../store/units/types';
import { PanelType } from '../../store/units/types';
import partition from 'util/partition';
import { getPasteLocations } from '../utils/getPasteLocation';
import type { UndoFromPortInterface, UndoToPortInterface } from './types';
import {
  handleNodeModifications,
  handleNodeInsertions,
  handleNodeRemovals,
} from './nodeHandlers';
import {
  handleLinkModifications,
  handleLinkInsertions,
  handleLinkRemovals,
} from './linkHandlers';

const PANEL_TYPES = new Set(Object.values(PanelType));

const getPanelUpdate = (panel: go.ObjectData): PanelInterface | null => {
  if (
    typeof panel.unitKey !== 'string' ||
    typeof panel.category !== 'string' ||
    typeof panel.loc !== 'string' ||
    !PANEL_TYPES.has(panel.category as PanelType)
  ) {
    return null;
  }

  return {
    loc: panel.loc,
    type: panel.category as PanelType,
    unitKey: panel.unitKey,
    height: panel.height,
    width: panel.width,
  };
};

const panelHasChanged = (
  currentPanel: PanelInterface | undefined,
  panelUpdate: PanelInterface,
) =>
  currentPanel?.loc !== panelUpdate.loc ||
  currentPanel?.height !== panelUpdate.height ||
  currentPanel?.width !== panelUpdate.width;

const handlePanelUpdates = (
  changedUnits: go.ObjectData[],
  removedNodeKeys: (go.Key | undefined)[] = [],
) => {
  const removedNodeKeySet = new Set(
    removedNodeKeys
      .map((key) => key?.toString())
      .filter((key): key is string => typeof key === 'string'),
  );
  const { panels: currentPanels, units } =
    store.getState().unitConnGroup.present.unitState;
  const unitKeys = new Set(
    units
      .map((unit) => unit.key)
      .filter((key): key is string => typeof key === 'string'),
  );
  const [panels, nonPanels] = partition(changedUnits, (u) => u.isPanel);

  const updatedPanels = panels.reduce<PanelInterface[]>((acc, panel) => {
    const panelUpdate = getPanelUpdate(panel);

    if (!panelUpdate) {
      return acc;
    }

    if (
      !unitKeys.has(panelUpdate.unitKey) ||
      removedNodeKeySet.has(panelUpdate.unitKey)
    ) {
      return acc;
    }

    const currentPanel = currentPanels[panelUpdate.unitKey]?.[panelUpdate.type];

    if (!panelHasChanged(currentPanel, panelUpdate)) {
      return acc;
    }

    return [...acc, panelUpdate];
  }, []);

  if (updatedPanels.length === 0) {
    return;
  }

  const panelMap: PanelMapInterface = updatedPanels.reduce((acc, curr) => {
    return {
      ...acc,
      [curr.unitKey]: {
        ...acc[curr.unitKey],
        [curr.type]: {
          loc: curr.loc,
          type: curr.type,
          height: curr.height,
          width: curr.width,
        },
      },
    };
  }, {} as PanelMapInterface);

  const panelAction =
    nonPanels.length === 0 ? updatePanelsWithHistory : updatePanels;

  store.dispatch(panelAction(panelMap));
};

export const handleModelChange = (e: go.IncrementalData) => {
  const changedUnits = e.modifiedNodeData;
  const insertedNodeKeys = e.insertedNodeKeys;
  const modifiedNodeData = e.modifiedNodeData;
  const removedNodeKeys = e.removedNodeKeys;
  const insertedLinkKeys = e.insertedLinkKeys;
  const modifiedLinkData = e.modifiedLinkData;
  const removedLinkKeys = e.removedLinkKeys;
  const modifiedNodeMap = new Map<go.Key, go.ObjectData>();
  const modifiedLinkMap = new Map<go.Key, go.ObjectData>();
  const newFromPortKeysMap = new Map<string, UndoFromPortInterface[]>();
  const newToPortKeysMap = new Map<string, UndoToPortInterface[]>();
  /**
   * 1. Check if node is a copied unit, if so, create new port keys for all ports, save these to a map relating to the nodeKey
   * 2. Check if link is a copied link, if so, check in the newPortKeys map and add the new connection with new from and toPort keys
   */
  batch(() => {
    if (modifiedNodeData) {
      modifiedNodeData.forEach((node: go.ObjectData) => {
        modifiedNodeMap.set(node.key, node);
      });

      handleNodeModifications(modifiedNodeData);
    }

    const nodes = (insertedNodeKeys ?? []).reduce(
      (acc: go.ObjectData[], curr) => {
        const node = modifiedNodeMap.get(curr);
        if (!node || node.isPanel) {
          return acc;
        }
        return [...acc, node];
      },
      [],
    );

    const pasteLocations = getPasteLocations(nodes);

    if (insertedNodeKeys) {
      handleNodeInsertions(
        insertedNodeKeys,
        modifiedNodeMap,
        pasteLocations,
        newFromPortKeysMap,
        newToPortKeysMap,
      );
    }

    if (removedNodeKeys) {
      handleNodeRemovals(removedNodeKeys);
    }
  });

  if (modifiedLinkData) {
    modifiedLinkData.forEach((link: go.ObjectData) => {
      modifiedLinkMap.set(link.key, link);
    });

    handleLinkModifications(modifiedLinkData);
  }

  if (insertedLinkKeys) {
    handleLinkInsertions(
      insertedLinkKeys,
      modifiedLinkMap,
      newFromPortKeysMap,
      newToPortKeysMap,
    );
  }

  if (removedLinkKeys) {
    handleLinkRemovals(removedLinkKeys, removedNodeKeys);
  }

  if (changedUnits) {
    handlePanelUpdates(changedUnits, removedNodeKeys);
  }
};
